import React, { useState, useMemo, useEffect } from 'react';
import { useGetSiteData } from 'components/SiteDetails/helper';
import { useGetIDsFromURL } from 'components/DeviceDetails/helper';
import { useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Card } from 'components/shadcn/ui/card';
import { Button } from 'components/shadcn/ui/button';
import HGFormSubmitBtn from 'components/HGForm/HGFormSubmitBtn';
import HGError from 'components/HGForm/HGError';
import { addSpaceBeforeEveryCapitalLetter } from 'components/HGForm/helpers';
import getValidationSchema from 'components/HGForm/formValidations';
import DeleteModal from './DeleteModal';
import HGSuccessCard from 'components/HGSuccessCard';
import {
    useVerifyAdmin,
    useVerifyCompanyAdmin,
    useVerifyCustomer,
    useJwtToken,
    API_ENDPOINT,
} from 'helper';
import HGFormInput from 'components/HGForm/HGFormInput';
import { useQueryClient } from 'react-query';
import AddressFields from 'components/HGForm/AddressFields';
import { parseAddressString } from 'components/HGForm/helpers';
import { stringifyAddressObject } from 'components/HGForm/helpers';
import SiteDetails from '..';

const EditSite = () => {
    const queryClient = useQueryClient();
    const { Site_ID } = useGetIDsFromURL();
    const { data: siteData } = useGetSiteData(Site_ID);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [deleted, setDeleted] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const isAdmin = useVerifyAdmin();
    const isCompanyAdmin = useVerifyCompanyAdmin();
    const isCustomer = useVerifyCustomer();
    const token = useJwtToken();
    const navigate = useNavigate();

    const [initialFieldsValues, setInitialFieldsValues] = useState({
        name: '',
        email: '',
        installerEmail: '',
        companyName: '',
        adress: {
            address: '',
            city: '',
            stateProvince: '',
            postalCode: '',
            country: 'United States',
            countryCode: 'US',
        },
    });

    useEffect(() => {
        if (siteData) {
            const { CompanyInfo } = siteData;
            const parsedAddress = parseAddressString(siteData?.Address);
            setInitialFieldsValues({
                name: siteData.Name || '',
                email: siteData?.CustomerEmail || '',
                installerEmail: siteData?.InstallerEmail || '',
                companyName: CompanyInfo?.Name || '',
                address: parsedAddress,
            });
        }
    }, [siteData]);

    const fieldKeysArray = Object.keys(initialFieldsValues);
    const validationSchema = useMemo(() => {
        const schemaShape = fieldKeysArray.reduce((acc, key) => {
            const schema = getValidationSchema(key);
            const fieldLabel = addSpaceBeforeEveryCapitalLetter(key);
            acc[key] = schema.required(`${fieldLabel} is required`);
            return acc;
        }, {});
        return Yup.object().shape(schemaShape);
    }, [fieldKeysArray]);

    const save = async (values) => {
        setErrorMessage(null);
        setSubmitting(true);
        try {
            const newAddress = stringifyAddressObject(values.address);
            const addressChanged = newAddress !== siteData.Address;
            const installerEmailChanged =
                values.installerEmail?.toLowerCase() !==
                siteData.InstallerEmail?.toLowerCase();
            const customerEmailChanged =
                values.email?.toLowerCase() !==
                siteData.CustomerEmail?.toLowerCase();
            const input = {
                Site_ID,
                Address: siteData.Address,
                Name: values.name,
                _version: siteData?._version,
                ...(addressChanged ? { newAddress } : {}),
                ...(installerEmailChanged
                    ? { InstallerEmail: values.installerEmail?.toLowerCase() }
                    : {}),
                ...(customerEmailChanged
                    ? { CustomerEmail: values.email?.toLowerCase() }
                    : {}),
            };
            const updatedSite = await updateSite(input, token);
            if (updatedSite) {
                setSuccess(true);
            }
        } catch (error) {
            console.log('Error updating site: ', error);
            setErrorMessage(error.message);
        } finally {
            await queryClient.invalidateQueries(['site', Site_ID]);
            setSubmitting(false);
        }
    };

    const onDelete = async () => {
        setErrorMessage(null);
        setSubmitting(true);
        setModalOpen(false);
        try {
            const input = {
                Site_ID,
                Address: siteData.Address,
                Archived: true,
                _version: siteData?._version,
            };
            const deletedSite = await deleteSite(input, token);
            if (deletedSite) {
                setDeleted(true);
            }
        } catch (error) {
            console.log('Error deleting site: ', error);
            setErrorMessage('Error deleting site');
        } finally {
            queryClient.invalidateQueries(['site', Site_ID]);
            queryClient.invalidateQueries(['sites']);
            setSubmitting(false);
        }
    };

    if (deleted) {
        return (
            <HGSuccessCard
                buttons={[
                    {
                        onClick: () => navigate('/sites'),
                        buttonText: 'Return to Sites',
                    },
                ]}
            >
                Site Deleted Successfully
            </HGSuccessCard>
        );
    }

    if (success) {
        return (
            <HGSuccessCard
                buttons={[
                    {
                        onClick: () => navigate(`/sites/${Site_ID}`),
                        buttonText: 'Return to Site Profile',
                    },
                ]}
            >
                Site Updated Successfully
            </HGSuccessCard>
        );
    }

    const disabledField = (key) => {
        if (key === 'name') return false;
        if (key === 'email') return isCustomer;
        if (key === 'installerEmail') return !(isAdmin || isCompanyAdmin);
        return true;
    };

    const getFieldName = (key) => (key === 'email' ? 'Customer Email' : key);

    return (
        <Card className="space-y-5 p-5">
            <h4 className="mb-3 text-lg font-semibold">Edit Site</h4>
            <Formik
                initialValues={initialFieldsValues}
                validationSchema={validationSchema}
                onSubmit={save}
                validateOnBlur={false}
                enableReinitialize
            >
                <Form>
                    <div className="my-5 flex flex-col gap-5">
                        {fieldKeysArray.map((key) => {
                            if (key === 'address') return null;
                            return (
                                <HGFormInput
                                    key={key}
                                    name={key}
                                    label={addSpaceBeforeEveryCapitalLetter(
                                        getFieldName(key)
                                    )}
                                    variant="standard"
                                    disabled={disabledField(key)}
                                />
                            );
                        })}
                    </div>
                    <AddressFields />

                    {errorMessage && (
                        <HGError errorMessage={errorMessage} className="mb-3" />
                    )}
                    <div className="mt-5 flex flex-col gap-2 md:flex-row md:justify-between">
                        {(isAdmin || isCompanyAdmin) && (
                            <Button
                                onClick={() => setModalOpen(true)}
                                className="w-full md:w-auto"
                                variant="destructive"
                                type="button"
                                disabled={submitting}
                            >
                                Delete Site
                            </Button>
                        )}
                        <div className="flex flex-col gap-2 md:flex-row">
                            <Button
                                variant="outline"
                                type="button"
                                onClick={() => navigate(`/sites/${Site_ID}`)}
                                className="w-full md:w-auto"
                                disabled={submitting}
                            >
                                Cancel
                            </Button>
                            <HGFormSubmitBtn
                                loading={submitting}
                                className="w-full md:w-auto"
                            >
                                Save
                            </HGFormSubmitBtn>
                        </div>
                    </div>
                </Form>
            </Formik>
            <DeleteModal
                modalOpen={modalOpen}
                modalClose={() => setModalOpen(false)}
                onDelete={onDelete}
            />
        </Card>
    );
};

const updateSite = async (input, token) => {
    const response = await fetch(
        `${API_ENDPOINT}/sites/${input.Site_ID}/update`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: token,
            },
            body: JSON.stringify(input),
        }
    );
    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update site');
    }
    return await response.json();
};

const deleteSite = async (siteInfo, token) => {
    const response = await fetch(
        `${API_ENDPOINT}/sites/${siteInfo.Site_ID}/delete`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: token,
            },
            body: JSON.stringify(siteInfo),
        }
    );
    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete site');
    }
    return await response.json();
};

export default EditSite;
